// --- Dependencies -------------------------------------------------------------------------------
import { useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import usePermission from "@/hooks/usePermission";
import useTranslation from "@/hooks/useTranslation";
import SettingsBrand from "@/UI/components/SettingsBrand";
import SettingsLanguages from "@/UI/components/SettingsLanguages";
import SettingsMenuTheme from "@/UI/components/SettingsMenuTheme";
import SettingsOrdering from "@/UI/components/SettingsOrdering";
import SettingsStoreLinks from "@/UI/components/SettingsStoreLinks";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type TyTab = "storeDetails" | "contact" | "ordering" | "menu";

// --- Constants ----------------------------------------------------------------------------------

/** Order shown, and the first one is where the screen opens. */
const TABS: TyTab[] = ["storeDetails", "contact", "ordering", "menu"];

// --- Logic --------------------------------------------------------------------------------------

export default function SettingsScreen() {
    const t = useTranslation();
    const { canView } = usePermission();
    const scroller = useRef<HTMLDivElement>(null);

    // Without any "settings" access the store details are not this client's business at all, so the
    // whole screen is dropped rather than shown disabled. The tab is hidden too — this guards the URL.
    const showBrand = canView("settings");

    const [tab, setTab] = useState<TyTab>("storeDetails");

    // --- Handlers --------------------------------------------------------------------------------

    const handleTab = (next: TyTab) => {
        setTab(next);
        // A card left halfway down would otherwise open the next tab somewhere in its middle.
        scroller.current?.scrollTo({ top: 0 });
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="settings-screen" ref={scroller}>
            {showBrand && (
                <div className="settings-screen-content">
                    <div className="settings-screen-tabs" role="tablist">
                        {TABS.map((entry) => (
                            <button
                                key={entry}
                                type="button"
                                role="tab"
                                aria-selected={tab === entry}
                                className="settings-screen-tab"
                                data-on={tab === entry}
                                onClick={() => handleTab(entry)}
                            >
                                {t.settings.tabs[entry]}
                            </button>
                        ))}
                    </div>

                    {/* Only the open tab is mounted: each card fetches its own copy of the store on
                        mount, and keeping four of them alive would be four requests for one screen. */}
                    {tab === "storeDetails" && <SettingsBrand />}
                    {tab === "contact" && <SettingsStoreLinks />}
                    {tab === "ordering" && <SettingsOrdering />}
                    {tab === "menu" && (
                        <>
                            <SettingsLanguages />
                            <SettingsMenuTheme />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
